/**
 * errorToast — a destructive toast that ALWAYS carries an action.
 *
 * Replaces bare `toast({ variant: 'destructive', description })` calls, which
 * left the user with an error and nothing to do. Per the product goal "every
 * surfaced error provides an action; worst case, report it":
 *   - if the caller can recover in place, it passes `onRetry` → a Retry button;
 *   - otherwise the toast offers Report → beacons the error to the backend
 *     (Cloud Logging) via reportClientError, so a failure the user hit isn't
 *     invisible to the team.
 *
 * A toast has a single action slot (and TOAST_LIMIT is 1), so it shows ONE
 * action: Retry when recoverable, else Report. Chat-answer errors do not use
 * this — they reuse message feedback (see MessageBubble / ErrorMessageCard).
 */

import { toast } from '@/hooks/use-toast';
import { ToastAction } from '@/components/ui/toast';
import { reportClientError, apiRequestContext } from '@/lib/error-reporter';
// Import the i18next singleton directly (not '@/i18n', whose module-load
// side-effect runs i18n.init()). The app configures this same singleton at
// startup, so labels are translated at runtime; in tests the t() defaultValue
// ('Retry'/'Report') applies. This keeps errorToast importable from a render
// path whose suite mocks react-i18next without initReactI18next.
import i18n from 'i18next';

export interface ErrorToastOptions {
  /** Human-readable, already-localized error text. */
  description: string;
  /** Origin tag for the report beacon, e.g. "createSession". */
  context: string;
  /** When provided, the toast shows Retry instead of Report. */
  onRetry?: () => void;
  /** Structured error code, forwarded to the report beacon. */
  errorCode?: string;
  /**
   * The caught error itself. When it is an ApiError, the report beacon carries
   * the failed call's method/path/status — the difference between a report an
   * engineer can act on and one that only names the screen. Pass it wherever
   * the error is in scope; `errorCode` stays supported for the call sites that
   * only have a code.
   */
  error?: unknown;
  /**
   * Message to BEACON when Report is tapped, if it must differ from the shown
   * `description`. Use when the description carries content unsafe to log
   * (e.g. a server-vend message that may include clinical text) — show it to
   * the nurse, beacon a generic line. Defaults to `description`.
   */
  reportMessage?: string;
  /**
   * The conversation a failed turn happened in, and the seq of the message
   * that started it. Forwarded to the beacon so the report can be joined to
   * the server side of the failure (its records, its prompt) — the keys a
   * turn failure has INSTEAD of an `error` with a stamped request.
   */
  sessionId?: string;
  turnSeq?: number;
  /** The pinned agent the turn ran on, from the session cache. */
  agentSlug?: string;
  agentVersion?: string;
}

/**
 * Client-refusal (4xx) codes that carry a specific, user-actionable reason →
 * localized i18n keys. The server messages are English (CLAUDE.md §5
 * English-only backend), so surfacing `err.message` raw put English body text
 * next to German toast chrome. Anything not listed falls back to the caller's
 * own generic line — never the raw server string on screen.
 *
 * Shared across every mutation that can be refused for one of these reasons
 * (session create, session→patient assign): the map lived in useSessions and
 * covered only the create path, so the SAME 403 on assign showed a generic
 * "could not assign" with no reason. One map, one meaning per code.
 */
const REFUSAL_MESSAGE_KEYS: Record<string, string> = {
  AGENT_ACCESS_REQUIRED: 'session.gate.agentAccessRequired',
  // The patient-framed action is no longer permitted. Home's "My shift" rail is
  // built from the shift ASSIGNMENT rows (Postgres), while the action is gated
  // on `patient viewer` (OpenFGA) — two authorities that legitimately disagree
  // the moment a grant lapses (an `active_shift` grant self-expires at its
  // shift_end, a handover revokes editor, an operator purges tuples). Without a
  // reason the nurse gets a dead end and a rail that keeps inviting the same
  // refusal.
  PATIENT_FORBIDDEN: 'session.gate.patientAccessLapsed',
};

/**
 * Resolve a refusal code to its localized explanation, falling back to the
 * caller's generic message when the code carries no specific reason (or when
 * the failure was a 5xx / network error with no code at all).
 */
// `fallback` is passed as i18next's defaultValue, not just returned on a missing
// map entry: this module imports the RAW i18next singleton (see the import note
// above), so on a path where the app has not run i18n.init() a bare t(key)
// returns the KEY — the nurse would read "session.gate.patientAccessLapsed".
// With defaultValue the worst case degrades to the caller's generic localized
// line, which is exactly the behaviour this replaced.
export function refusalMessage(code: string | undefined, fallback: string): string {
  const key = code ? REFUSAL_MESSAGE_KEYS[code] : undefined;
  return key ? i18n.t(key, { defaultValue: fallback }) : fallback;
}

export function errorToast(opts: ErrorToastOptions): void {
  const t = i18n.t.bind(i18n);
  const action = opts.onRetry ? (
    <ToastAction
      altText={t('actions.retry', 'Retry')}
      onClick={opts.onRetry}
    >
      {t('actions.retry', 'Retry')}
    </ToastAction>
  ) : (
    <ToastAction
      altText={t('actions.report', 'Report')}
      onClick={() => {
        reportClientError({
          context: `toast:${opts.context}`,
          message: opts.reportMessage ?? opts.description,
          errorCode: opts.errorCode,
          request: apiRequestContext(opts.error),
          sessionId: opts.sessionId,
          turnSeq: opts.turnSeq,
          agentSlug: opts.agentSlug,
          agentVersion: opts.agentVersion,
        });
        // Acknowledge the tap — Report was silent fire-and-forget before, so it
        // read as a dead button. TOAST_LIMIT is 1, so this replaces the error
        // toast with the confirmation.
        toast({
          description: t('actions.reported', 'Reported — thanks. The team can see it now.'),
        });
      }}
    >
      {t('actions.report', 'Report')}
    </ToastAction>
  );
  toast({ variant: 'destructive', description: opts.description, action });
}
